/*
 * Shared quick-add handler for the custom PurePicked product cards.
 *
 * ppAddToCart  - the original. Used by the .pp-card__atc text buttons on
 *                collections / Shop All / homepage. Opens the cart drawer.
 * ppCardQuickAdd - the SEARCH variant (.pp-card__cart icon buttons). Same POST,
 *                but it deliberately does NOT open the drawer, so a customer can
 *                add several items in a row without losing their result list.
 * CARD QUANTITY STEPPER (19 Sep 2026, bottom of this file) - what every product
 *                card now uses. "+ ADD" becomes "- n +" once the item is in the
 *                cart. ppCardQuickAdd is kept for anything that still calls it.
 *
 * Because nothing slides open on the search path, the add MUST announce itself
 * some other way. A silent add that only bumps a header counter reads as a dark
 * pattern - Nikit rejected it on 2 Sep 2026 in those words. So the search path
 * gives three signals: the button flips to a tick, the header cart icon pulses,
 * and a toast says "Added to cart" with UNDO and View cart. Undo is the point:
 * the customer can always reverse it in one tap. Do not remove it.
 *
 * ---- LATENCY, measured 2 Sep 2026 from Delhi ----
 *   plain GET /cart.js ................ ~600ms   (pure round trip, the floor)
 *   POST /cart/add.js ................. ~1250ms
 *   POST /cart/add.js WITH sections ... ~1500-2500ms
 * Asking Shopify to server-render cart-icon-bubble in the add request cost a
 * clear ~600ms, so we no longer do it: the count is written into the existing
 * bubble markup client-side instead, and the section is only fetched in the one
 * case where that markup does not exist yet (cart was empty, so the header is
 * still rendering icon-cart-empty.svg with no bubble at all).
 * Even so ~600ms of the remaining wait is just the distance to Shopify, which
 * is why the tick is OPTIMISTIC - see ppCardQuickAdd.
 */
(function () {
  if (window.ppAddToCart) return;

  function getSectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function refreshCartIcon(sections) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    if (sections && sections['cart-icon-bubble']) {
      var html = getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
      if (html !== null) {
        target.innerHTML = html;
        pulseCartIcon();
        return;
      }
    }

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + '?section_id=cart-icon-bubble')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var html = getSectionInnerHTML(text, '.shopify-section');
        if (html !== null) { target.innerHTML = html; pulseCartIcon(); }
      })
      .catch(function () {});
  }

  // Write the count straight into the bubble that is already on the page - no
  // server round trip. Returns false when the bubble does not exist, which
  // happens only when the cart was empty (the header is showing the empty cart
  // icon and Shopify has rendered no .cart-count-bubble at all); the caller
  // then falls back to a section fetch, off the critical path.
  function setCartCount(count) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return true; // no header cart on this page; nothing to do
    var bubble = target.querySelector('.cart-count-bubble');
    if (!bubble) return false;

    var visible = bubble.querySelector('span[aria-hidden]');
    if (visible) visible.textContent = count < 100 ? count : '';
    var sr = bubble.querySelector('.visually-hidden');
    if (sr) sr.textContent = count + ' items';
    pulseCartIcon();
    return true;
  }

  // A one-shot bounce on the header cart, so the eye is pulled to the thing that
  // just changed. The toast says what happened; this says where it went.
  function pulseCartIcon() {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    target.classList.remove('pp-cart-pulse');
    void target.offsetWidth;
    target.classList.add('pp-cart-pulse');
    setTimeout(function () { target.classList.remove('pp-cart-pulse'); }, 700);
  }

  // The mobile bottom nav (.pp-sticky-bar in layout/theme.liquid) renders its
  // cart badge server-side, so an AJAX add leaves it stale.
  function updateStickyBadge(count) {
    var bar = document.querySelector('.pp-sticky-bar');
    if (!bar) return;
    var target = null;
    bar.querySelectorAll('.pp-sticky-bar__item').forEach(function (item) {
      var icon = item.querySelector('.pp-sticky-bar__icon');
      if (icon && icon.textContent.trim() === 'shopping_bag') target = item;
    });
    if (!target) return;
    var badge = target.querySelector('.pp-sticky-bar__badge');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'pp-sticky-bar__badge';
        target.appendChild(badge);
      }
      badge.textContent = count;
      badge.setAttribute('aria-label', count + ' items');
    } else if (badge) {
      badge.remove();
    }
  }

  // `source` is not cosmetic: cart-drawer-items (cart.js) subscribes to
  // cartUpdate and re-fetches the whole cart-drawer section for any source
  // other than 'cart-items'. When we have just rendered the drawer ourselves
  // that second render is pure waste. So claim 'cart-items' on the drawer path,
  // and only there.
  function notifyCartUpdate(response, alreadyRendered) {
    try {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: alreadyRendered ? 'cart-items' : 'pp-card-product',
          productVariantId: response && response.variant_id,
          cartData: response,
        });
      }
    } catch (e) {
      /* cart events are informational */
    }
    document.dispatchEvent(new CustomEvent('pp:cart:updated', { detail: response }));
  }

  // BUG FIX 6 Sep 2026. The quantity box declares min="1" and nothing applied
  // it, so a 0 - or a blank, or a negative - was posted to /cart/add.js as
  // typed. Shopify answers that with HTTP 200 and an empty body: nothing is
  // added, yet the caller went on to claim it was. Clamp before we submit.
  function normalizeQuantity(value) {
    var qty = parseInt(value, 10);
    return isNaN(qty) || qty < 1 ? 1 : qty;
  }

  // BUG FIX 6 Sep 2026. HTTP 200 is not proof that anything was added - the
  // no-op add above answers 200 with `{}`. A real add always carries `id` (a
  // single line) or `items` (several), so require one of them before anything
  // claims success. Without this the green tick and the "Added to cart" toast,
  // Undo and all, fired on an add that never happened.
  function addSucceeded(data) {
    return !!(data && (data.id || data.items));
  }

  /* ---------------- toast ---------------- */

  var ppToastTimer = null;
  var ppToastTeardown = null;

  function ensureToastStyles() {
    if (document.getElementById('pp-toast-styles')) return;
    var s = document.createElement('style');
    s.id = 'pp-toast-styles';
    s.textContent = [
      '#pp-toast-host{position:fixed;left:0;right:0;bottom:2.4rem;z-index:10001;display:flex;justify-content:center;padding:0 1.6rem;pointer-events:none}',
      '@media screen and (max-width:749px){#pp-toast-host{bottom:calc(5.6rem + 1.2rem + env(safe-area-inset-bottom,0px))}}',
      // Entry is a CSS animation, not a class toggled on the next frame.
      // The class-toggle version was caught sitting at opacity 0 on 2 Sep -
      // requestAnimationFrame is throttled or deferred in enough situations
      // that a toast could silently never appear, which is the exact failure
      // this toast exists to prevent.
      '.pp-toast{pointer-events:auto;display:flex;align-items:center;gap:1.8rem;background:#3F3932;color:#fff;border-radius:9999px;padding:1.1rem 2.2rem;box-shadow:0 6px 24px rgba(0,0,0,.28);font-family:Manrope,system-ui,sans-serif;font-size:1.3rem;font-weight:600;line-height:1;max-width:100%;opacity:0;transform:translateY(1.2rem);animation:pp-toast-in .28s cubic-bezier(.4,0,.2,1) forwards}',
      '@keyframes pp-toast-in{to{opacity:1;transform:none}}',
      '.pp-toast.is-out{animation:none;opacity:0;transform:translateY(1.2rem);transition:opacity .25s ease,transform .25s ease}',
      '.pp-toast__msg{display:flex;align-items:center;gap:0.7rem;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
      // The announcement sits in its own visually hidden live region so the
      // Undo / View cart buttons can live outside it - see ppToast.
      '.pp-toast__live{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0}',
      '.pp-toast__actions{display:flex;align-items:center;gap:1.8rem;flex-shrink:0}',
      '.pp-toast__tick{font-family:"Material Symbols Outlined";font-variation-settings:"FILL" 1,"wght" 500,"GRAD" 0,"opsz" 20;font-size:1.7rem;line-height:1;color:#8CC63F;font-style:normal;font-weight:normal;-webkit-font-feature-settings:"liga";font-feature-settings:"liga"}',
      '.pp-toast--error .pp-toast__tick{color:#FFB4A2}',
      '.pp-toast__action{appearance:none;-webkit-appearance:none;background:none;border:0;margin:0;padding:0;color:#fff;font:inherit;text-decoration:underline;text-underline-offset:3px;cursor:pointer;white-space:nowrap;flex-shrink:0}',
      '.pp-toast__action:hover{opacity:.7}',
      '.pp-toast__action:focus-visible{outline:2px solid #fff;outline-offset:3px;border-radius:.2rem}',
      '@media screen and (max-width:480px){.pp-toast{gap:1.4rem;padding:1rem 1.8rem;font-size:1.25rem}.pp-toast__actions{gap:1.4rem}}',
      '@keyframes pp-cart-pulse{0%{transform:scale(1)}30%{transform:scale(1.22)}60%{transform:scale(.94)}100%{transform:scale(1)}}',
      '#cart-icon-bubble.pp-cart-pulse{display:inline-block;animation:pp-cart-pulse .6s cubic-bezier(.4,0,.2,1)}',
      '@media (prefers-reduced-motion:reduce){.pp-toast{animation-duration:.01ms}.pp-toast.is-out{transition-duration:.01ms}#cart-icon-bubble.pp-cart-pulse{animation:none}}',
    ].join('');
    document.head.appendChild(s);
  }

  // actions: [{label, onClick}] or [{label, href}]
  //
  // BUG FIX 6 Sep 2026. The host was the live region AND the box holding Undo
  // and View cart. Screen readers announce a live region's text but do not
  // reliably let anyone reach the controls inside one, and there was no
  // keyboard way out of the 5.5s auto-dismiss either, so Undo - the whole
  // point of this toast - was operable by pointer only. Now the announcement
  // has its own live region, kept in the DOM between toasts because a live
  // region inserted at the same instant as its text is announced unreliably;
  // the controls sit outside it in a plain sibling container; Escape
  // dismisses; and the clock holds while the toast is hovered or holds focus.
  // Focus is deliberately NOT moved here - that would yank a sighted mouse
  // user out of the page on every add.
  function ppToast(message, actions, isError) {
    ensureToastStyles();
    var host = document.getElementById('pp-toast-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'pp-toast-host';
      document.body.appendChild(host);
    }

    var live = host.querySelector('.pp-toast__live');
    if (!live) {
      live = document.createElement('p');
      live.className = 'pp-toast__live';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      host.appendChild(live);
    }

    if (ppToastTeardown) ppToastTeardown();

    var el = document.createElement('div');
    el.className = 'pp-toast' + (isError ? ' pp-toast--error' : '');
    // Named group, so tabbing to Undo says what it would undo.
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', message);

    var msg = document.createElement('span');
    msg.className = 'pp-toast__msg';
    msg.setAttribute('aria-hidden', 'true'); // spoken by .pp-toast__live instead
    var tick = document.createElement('span');
    tick.className = 'pp-toast__tick';
    tick.setAttribute('aria-hidden', 'true');
    tick.textContent = isError ? 'error' : 'check_circle';
    msg.appendChild(tick);
    msg.appendChild(document.createTextNode(message));
    el.appendChild(msg);

    var controls = document.createElement('span');
    controls.className = 'pp-toast__actions';
    (actions || []).forEach(function (a) {
      var node;
      if (a.href) {
        node = document.createElement('a');
        node.href = a.href;
      } else {
        node = document.createElement('button');
        node.type = 'button';
        node.addEventListener('click', function (ev) {
          ev.preventDefault();
          a.onClick(ev);
        });
      }
      node.className = 'pp-toast__action';
      node.textContent = a.label;
      node.setAttribute('aria-label', a.ariaLabel || a.label);
      controls.appendChild(node);
    });
    if (controls.childNodes.length) el.appendChild(controls);

    host.appendChild(el);
    live.textContent = message;

    var remaining = 5500;
    var startedAt = 0;
    var hovered = false;
    var focused = false;

    function drop() {
      if (el.parentNode) el.parentNode.removeChild(el);
    }

    function release() {
      clearTimeout(ppToastTimer);
      ppToastTimer = null;
      document.removeEventListener('keydown', onKeydown);
      if (ppToastTeardown === teardown) ppToastTeardown = null;
    }

    function teardown() {
      release();
      drop();
    }

    function hide() {
      release();
      el.classList.add('is-out');
      setTimeout(drop, 300);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') hide();
    }

    function startClock() {
      startedAt = Date.now();
      ppToastTimer = setTimeout(hide, remaining);
    }

    // Hold what is left of the clock rather than restart it, so a pointer
    // resting on the toast cannot keep it up forever; the floor stops it
    // vanishing the instant a pointer or focus leaves.
    function holdClock() {
      if (!ppToastTimer) return;
      clearTimeout(ppToastTimer);
      ppToastTimer = null;
      remaining = Math.max(1500, remaining - (Date.now() - startedAt));
    }

    function resumeClock() {
      if (hovered || focused || ppToastTimer) return;
      startClock();
    }

    el.addEventListener('mouseenter', function () { hovered = true; holdClock(); });
    el.addEventListener('mouseleave', function () { hovered = false; resumeClock(); });
    el.addEventListener('focusin', function () { focused = true; holdClock(); });
    el.addEventListener('focusout', function (ev) {
      focused = !!(ev.relatedTarget && el.contains(ev.relatedTarget));
      resumeClock();
    });
    document.addEventListener('keydown', onKeydown);

    ppToastTeardown = teardown;
    startClock();

    return el;
  }
  window.ppToast = ppToast;

  /* ---------------- the two add handlers ---------------- */

  window.ppAddToCart = function (variantId, btn, quantity) {
    var qty = normalizeQuantity(quantity);
    var orig = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var drawer = document.querySelector('cart-drawer');
    var sections = drawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: qty }],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (result) {
        // A 200 that added nothing is a failure, not a success - addSucceeded.
        if (!result.ok || !addSucceeded(result.data)) {
          if (btn) { btn.textContent = orig; btn.disabled = false; }
          var why = (result.data && (result.data.description || result.data.message)) || 'Could not add that — please try again';
          ppToast(why, [], true);
          return;
        }
        var rendered = false;
        if (drawer && typeof drawer.renderContents === 'function' && result.data.sections && result.data.sections['cart-drawer']) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result.data);
          rendered = true;
        } else {
          refreshCartIcon(result.data.sections);
        }
        notifyCartUpdate(result.data, rendered);
        if (btn) {
          btn.textContent = 'Added ✓';
          setTimeout(function () { btn.textContent = orig; btn.disabled = false; }, 1800);
        }
      })
      .catch(function () {
        if (btn) { btn.textContent = orig; btn.disabled = false; }
      });
  };

  function undoLine(key, newQuantity) {
    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
    fetch(changeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQuantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!setCartCount(cart.item_count)) refreshCartIcon(null);
        updateStickyBadge(cart.item_count);
        notifyCartUpdate(cart, false);
        ppToast('Removed from cart', []);
      })
      .catch(function () {
        ppToast('Could not undo — open your cart to remove it', [
          { label: 'View cart', href: (window.routes && window.routes.cart_url) || '/cart' },
        ], true);
      });
  }

  /*
   * Search icon quick-add. Differences from ppAddToCart:
   *  1. Never renders the cart-drawer section, so the drawer stays shut.
   *  2. The button's textContent IS a Material Symbols ligature, so state is
   *     shown by swapping the ligature, never by writing a word into it.
   *  3. The tick is OPTIMISTIC - it fires on tap, not on the server's reply.
   *
   * On (3): about 600ms of the round trip is just the distance to Shopify, and
   * making someone watch a spinner for it is what felt broken. So the button
   * acknowledges the TAP immediately. Nothing irreversible is asserted early -
   * the toast, which is the actual claim that the item is in the cart and the
   * thing carrying Undo, still waits for real confirmation. If the add fails
   * the button reverts to the plus and the red error toast says why, so a
   * failure is never left looking like a success.
   */
  window.ppCardQuickAdd = function (variantId, btn, quantity) {
    if (!btn || btn.dataset.ppBusy === '1') return;
    var qty = normalizeQuantity(quantity);
    btn.dataset.ppBusy = '1';
    btn.disabled = true;

    var icon = btn.querySelector('.pp-card__cart-icon') || btn;
    var origIcon = icon.textContent;

    // Optimistic: acknowledge the tap now.
    btn.classList.add('is-added');
    icon.textContent = 'check';

    function reset() {
      icon.textContent = origIcon;
      btn.classList.remove('is-added');
      btn.disabled = false;
      btn.dataset.ppBusy = '0';
    }

    // Shopify's own message is more useful than ours when it is a real reason
    // ("You can only add 3 to your cart"), so pass it through when present.
    function fail(data) {
      btn.classList.remove('is-added');
      icon.textContent = 'error';
      var why = (data && (data.description || data.message)) || 'Could not add that — please try again';
      ppToast(why, [], true);
      setTimeout(reset, 2200);
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    // No `sections` here on purpose - see the latency note at the top.
    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (result) {
        // A 200 that added nothing is a failure, not a success - addSucceeded.
        if (!result.ok || !addSucceeded(result.data)) { fail(result.data); return; }

        notifyCartUpdate(result.data, false);

        var line = (result.data.items && result.data.items[0]) || result.data;
        var actions = [];
        // Undo drops the line back to what it was BEFORE this click, so a second
        // copy of something already in the cart is not wiped out entirely.
        // BUG FIX 6 Sep 2026. This subtracted a hardcoded 1, which was only
        // ever right because this path adds a single unit; any add of more
        // left the surplus in the cart while the toast said "Removed from
        // cart". Undo now reverses exactly the quantity this add submitted.
        if (line && line.key && typeof line.quantity === 'number') {
          actions.push({
            label: 'Undo',
            onClick: function () { undoLine(line.key, Math.max(0, line.quantity - qty)); },
          });
        }
        actions.push({ label: 'View cart', href: cartUrl });
        ppToast('Added to cart', actions);

        setTimeout(reset, 1200);

        // Counts come from /cart.js rather than a server-rendered section.
        fetch(cartUrl + '.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (!setCartCount(cart.item_count)) refreshCartIcon(null);
            updateStickyBadge(cart.item_count);
          })
          .catch(function () { refreshCartIcon(null); });
      })
      .catch(function () { fail(null); });
  };

  /*
   * Keep the type-ahead dropdown open when the icon is clicked.
   *
   * Dawn's predictive search closes on focus loss. Clicking a <button> moves
   * focus out of the search input, so the whole dropdown vanished the instant
   * you added something - no tick, no toast, nothing, which is precisely what
   * made the add feel like a trick. Cancelling the default on mousedown
   * prevents the focus shift; the click event still fires, so the add (and, on
   * the multi-variant spans, the card's own link) is unaffected.
   *
   * Done here by delegation rather than an inline attribute on the card, so the
   * card snippet stays free of workarounds for another component's behaviour.
   */
  document.addEventListener(
    'mousedown',
    function (e) {
      if (!e.target || !e.target.closest) return;
      // [data-pp-qty] since 19 Sep: the stepper's - and + are not .pp-card__cart.
      if (e.target.closest('.pp-card__cart, [data-pp-qty]')) e.preventDefault();
    },
    true
  );

  /* =====================================================================
   * CARD QUANTITY STEPPER - PP1.6, 19 Sep 2026 (Nikit: layout B, search S1)
   *
   * Customers could not tell whether a product was already in their cart,
   * and could not change the quantity from the card. Every single-variant
   * in-stock card now renders an "Add" pill in [data-pp-qty]; once the
   * variant is in the cart the pill becomes "- n +" and stays that way.
   *
   * WHERE THE NUMBER COMES FROM. Never from Liquid. Collection pages are
   * cached, and cart-dependent markup on a cached page can show one shopper
   * another shopper's cart. Cards always render "Add"; the browser's saved
   * copy of the cart paints the numbers instantly, then /cart.js confirms.
   *
   * RAZORPAY'S SIDECART IS THE CART (Magic Checkout stays, 19 Sep). It is a
   * cross-origin iframe and tells us nothing when a quantity changes inside
   * it. So the cards re-read /cart.js when its container leaves the DOM
   * (see sections/pp-cart-collision-fix.liquid for the container), when the
   * tab becomes visible again, and on a back/forward restore.
   *
   * WRITES are /cart/update.js with ABSOLUTE quantities, batched: taps move
   * the number at once, and 400ms after the last tap every changed variant
   * goes to Shopify in ONE request. Absolute quantities mean a retry or a
   * burst of taps can never double-add. One request at a time.
   *
   * HONESTY RULE (2 Sep, dark patterns): the number moving on tap is only an
   * acknowledgement of the tap. The "Added to cart" toast is the claim, so it
   * waits for Shopify's answer - and only a 0 -> n change gets one. A failure
   * snaps the number back to what Shopify holds and says why, in red.
   * ===================================================================== */

  var PP_QTY_CACHE = 'pp:cart-qty:v1';
  var qtyHeld = {};        // variant id -> quantity Shopify holds
  var qtyWant = {};        // variant id -> quantity the shopper asked for, not yet confirmed
  var qtyBefore = {};      // variant id -> quantity before this burst of taps (decides the toast)
  var qtyUndo = {};        // variant id -> true while an Undo is in flight
  var qtyFlushTimer = null;
  var qtyInflight = false;
  var qtySelfEvent = false;

  function qtyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function qtyCartUrl() { return (window.routes && window.routes.cart_url) || '/cart'; }

  function qtyReadCache() {
    try {
      var v = JSON.parse(window.localStorage.getItem(PP_QTY_CACHE) || 'null');
      return v && v.q ? v.q : null;
    } catch (e) { return null; }
  }
  function qtyWriteCache() {
    try { window.localStorage.setItem(PP_QTY_CACHE, JSON.stringify({ t: Date.now(), q: qtyHeld })); } catch (e) {}
  }

  // A variant can sit on more than one line (it should not here, but a line
  // with properties would do it), so sum rather than overwrite.
  function qtyFromCart(cart) {
    var q = {};
    ((cart && cart.items) || []).forEach(function (it) {
      var id = String(it.variant_id);
      q[id] = (q[id] || 0) + it.quantity;
    });
    return q;
  }

  function qtyShown(id) { return id in qtyWant ? qtyWant[id] : (qtyHeld[id] || 0); }

  function qtyMax(slot) {
    var m = parseInt(slot.getAttribute('data-max'), 10);
    return isNaN(m) ? Infinity : m;
  }

  function qtySlots(id) {
    return document.querySelectorAll('[data-pp-qty][data-variant-id="' + id + '"]');
  }

  function qtyEsc(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function qtyStepperHtml(slot) {
    var name = qtyEsc(slot.getAttribute('data-name'));
    return '<div class="pp-card__step" role="group" aria-label="Quantity of ' + name + ' in cart">'
      + '<button type="button" class="pp-card__step-btn" data-pp-qty-act="dec" aria-label="One less ' + name + '">'
      + '<span class="pp-card__step-icon" aria-hidden="true">remove</span></button>'
      + '<span class="pp-card__step-n" aria-live="polite"><span class="pp-card__step-v"></span>'
      + '<span class="visually-hidden"> in cart</span></span>'
      + '<button type="button" class="pp-card__step-btn" data-pp-qty-act="inc" aria-label="One more ' + name + '">'
      + '<span class="pp-card__step-icon" aria-hidden="true">add</span></button>'
      + '</div>';
  }

  // Paint one slot to match qtyShown. The stepper is built once and then only
  // its number and states change, so a keyboard user's focus is not thrown
  // away on every press.
  function qtyPaint(slot) {
    var id = slot.getAttribute('data-variant-id');
    if (slot.__ppAddHtml == null) slot.__ppAddHtml = slot.innerHTML;
    var n = qtyShown(id);
    var max = qtyMax(slot);
    var step = slot.querySelector('.pp-card__step');
    var hadFocus = slot.contains(document.activeElement);

    if (n > 0) {
      if (!step) {
        slot.innerHTML = qtyStepperHtml(slot);
        step = slot.querySelector('.pp-card__step');
        if (hadFocus) step.querySelector('[data-pp-qty-act="inc"]').focus();
      }
      step.querySelector('.pp-card__step-v').textContent = n;
      var inc = step.querySelector('[data-pp-qty-act="inc"]');
      if (n >= max) inc.setAttribute('aria-disabled', 'true');
      else inc.removeAttribute('aria-disabled');
      step.classList.toggle('is-pending', id in qtyWant);
      slot.classList.add('is-in-cart');
    } else if (step) {
      slot.innerHTML = slot.__ppAddHtml;
      slot.classList.remove('is-in-cart');
      if (hadFocus) {
        var add = slot.querySelector('[data-pp-qty-act="add"]');
        if (add) add.focus();
      }
    }

    // "Only N in stock" sits in the price row, so it costs no height.
    var card = slot.closest('.pp-card');
    var cap = card && card.querySelector('[data-pp-qty-cap]');
    if (cap) {
      var capped = n > 0 && n >= max;
      if (capped) cap.textContent = 'Only ' + max + ' in stock';
      cap.hidden = !capped;
    }
  }

  function qtyPaintId(id) { qtySlots(id).forEach(qtyPaint); }
  function qtyPaintAll() { document.querySelectorAll('[data-pp-qty]').forEach(qtyPaint); }

  function qtyShake(id) {
    qtySlots(id).forEach(function (slot) {
      var step = slot.querySelector('.pp-card__step');
      if (!step) return;
      step.classList.remove('is-fail');
      void step.offsetWidth;
      step.classList.add('is-fail');
      setTimeout(function () { step.classList.remove('is-fail'); }, 400);
    });
  }

  // Header bubble + mobile bottom-nav badge. Quiet when the number has not
  // changed, so a background re-sync never bounces the cart icon.
  var qtyLastCount = null;
  function qtyCounts(count) {
    if (typeof count !== 'number' || count === qtyLastCount) return;
    var first = qtyLastCount === null;
    qtyLastCount = count;
    if (first) {
      // Page just loaded: the server-rendered counts are already right unless
      // the cart changed elsewhere. Only touch them if they disagree.
      var vis = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
      var shownCount = vis ? parseInt(vis.textContent, 10) || 0 : 0;
      if (shownCount === count) return;
    }
    if (!setCartCount(count)) refreshCartIcon(null);
    updateStickyBadge(count);
  }

  function qtyAnnounce(cart) {
    qtySelfEvent = true;
    try { document.dispatchEvent(new CustomEvent('pp:cart:updated', { detail: cart })); }
    finally { qtySelfEvent = false; }
  }

  // Goes through window.ppToast so pp-cart-bridge's wrapper can turn the
  // View cart link into "open Razorpay's sidecart".
  function qtyToast(msg, actions, isError) {
    return (window.ppToast || ppToast)(msg, actions, isError);
  }

  function qtyFailMessage(data) {
    return (data && (data.description || data.message)) || 'Could not update your cart - please try again';
  }

  function qtySet(id, n) {
    if (!(id in qtyBefore)) qtyBefore[id] = qtyHeld[id] || 0;
    qtyWant[id] = n;
    qtyPaintId(id);
    clearTimeout(qtyFlushTimer);
    qtyFlushTimer = setTimeout(qtyFlush, 400);
  }

  function qtyFlush() {
    qtyFlushTimer = null;
    if (qtyInflight) return; // picked up again when the request in flight settles

    var sent = {};
    var ids = [];
    Object.keys(qtyWant).forEach(function (id) {
      if (qtyWant[id] === (qtyHeld[id] || 0)) {
        // Tapped back to where it started: nothing to send.
        delete qtyWant[id];
        delete qtyBefore[id];
        delete qtyUndo[id];
        qtyPaintId(id);
      } else {
        sent[id] = qtyWant[id];
        ids.push(id);
      }
    });
    if (!ids.length) return;

    qtyInflight = true;
    fetch(qtyRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: sent }),
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (res) {
        // A real answer is the whole cart. Anything else is a failure, even
        // on HTTP 200 - see addSucceeded above for why 200 proves nothing.
        if (!res.ok || !res.data || !Array.isArray(res.data.items)) throw res.data || {};
        qtySettle(res.data, sent);
      })
      .catch(function (err) {
        ids.forEach(function (id) {
          if (qtyWant[id] === sent[id]) {
            delete qtyWant[id];
            delete qtyBefore[id];
            delete qtyUndo[id];
          }
          qtyPaintId(id);
          qtyShake(id);
        });
        qtyToast(qtyFailMessage(err), [], true);
      })
      .then(function () {
        qtyInflight = false;
        if (Object.keys(qtyWant).length && !qtyFlushTimer) qtyFlushTimer = setTimeout(qtyFlush, 400);
      });
  }

  function qtySettle(cart, sent) {
    qtyHeld = qtyFromCart(cart);
    qtyWriteCache();

    Object.keys(sent).forEach(function (id) {
      var got = qtyHeld[id] || 0;

      // Shopify caps a quantity at what is in stock instead of refusing it.
      // Our data-max comes from a cached page and can be stale, so believe
      // Shopify, lower the cap everywhere and say so.
      if (got < sent[id]) {
        qtySlots(id).forEach(function (s) { s.setAttribute('data-max', got); });
        if (qtyWant[id] === sent[id]) delete qtyWant[id];
        qtyToast(got ? 'Only ' + got + ' in stock - your cart has ' + got : 'That just sold out', [], true);
      } else if (qtyWant[id] === sent[id]) {
        delete qtyWant[id];
      }

      if (id in qtyWant) return; // more taps since - that burst settles later
      var before = qtyBefore[id];
      var wasUndo = qtyUndo[id];
      delete qtyBefore[id];
      delete qtyUndo[id];

      if (wasUndo) {
        qtyToast('Removed from cart', []);
      } else if (before === 0 && got > 0) {
        qtyToast('Added to cart', [
          { label: 'Undo', onClick: function () { qtyUndo[id] = true; qtySet(id, 0); } },
          { label: 'View cart', href: qtyCartUrl() },
        ]);
      }
    });

    qtyPaintAll();
    qtyCounts(cart.item_count);
    qtyAnnounce(cart);
  }

  // Re-read the cart. Variants with taps not yet confirmed keep showing what
  // the shopper asked for - qtyShown prefers qtyWant.
  var qtySyncing = false;
  function qtySync() {
    if (qtySyncing) return;
    qtySyncing = true;
    fetch(qtyCartUrl() + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        qtyHeld = qtyFromCart(cart);
        qtyWriteCache();
        qtyPaintAll();
        qtyCounts(cart.item_count);
      })
      .catch(function () {})
      .then(function () { qtySyncing = false; });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-pp-qty-act]');
    if (!btn) return;
    var slot = btn.closest('[data-pp-qty]');
    if (!slot) return;
    // Never let the tap reach the card's overlay link underneath.
    e.preventDefault();
    e.stopPropagation();
    if (btn.getAttribute('aria-disabled') === 'true') return;

    var id = slot.getAttribute('data-variant-id');
    var cur = qtyShown(id);
    var next = btn.getAttribute('data-pp-qty-act') === 'dec' ? cur - 1 : cur + 1;
    if (next < 0 || next > qtyMax(slot)) return;
    qtySet(id, next);
  });

  // Somebody else changed the cart: a PDP add or an Undo from pp-cart-bridge,
  // or the old quick-add path. A whole cart in the event is applied as is;
  // a single line (what /cart/add.js returns) means re-read.
  document.addEventListener('pp:cart:updated', function (e) {
    if (qtySelfEvent) return;
    var d = e.detail;
    if (d && Array.isArray(d.items) && typeof d.item_count === 'number') {
      qtyHeld = qtyFromCart(d);
      qtyWriteCache();
      qtyPaintAll();
      qtyCounts(d.item_count);
    } else {
      qtySync();
    }
  });

  window.addEventListener('pageshow', function (e) { if (e.persisted) qtySync(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') qtySync();
  });

  // One observer does two jobs: paints cards that arrive after load
  // (predictive search results, lazy carousels) and tracks Razorpay's
  // sidecart, the only sign we get that the shopper may have changed
  // quantities inside it.
  //
  // The sidecart container is NOT removed when it closes - Razorpay only
  // hides it (PP1.6 code review, 19 Sep). So "closed" means the container
  // is gone OR no longer displayed, and the container's own style/class
  // changes are watched once it exists.
  var qtyRzpOpen = false;
  var qtyRzpWatched = null;
  function qtyRzpCheck() {
    var el = document.querySelector('.razorpay-sidecart-container');
    if (el && el !== qtyRzpWatched && window.MutationObserver) {
      qtyRzpWatched = el;
      new MutationObserver(qtyRzpCheck).observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    }
    var open = false;
    if (el && el.isConnected) {
      var cs = window.getComputedStyle(el);
      open = cs.display !== 'none' && cs.visibility !== 'hidden';
    }
    if (qtyRzpOpen && !open) qtySync();
    qtyRzpOpen = open;
  }
  function qtyObserve() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-pp-qty]')) qtyPaint(node);
          else if (node.querySelectorAll) node.querySelectorAll('[data-pp-qty]').forEach(qtyPaint);
        }
      }
      qtyRzpCheck();
    }).observe(document.body, { childList: true, subtree: true });
    qtyRzpCheck();
  }

  function qtyInit() {
    var cached = qtyReadCache();
    if (cached) { qtyHeld = cached; qtyPaintAll(); }
    qtyObserve();
    qtySync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', qtyInit);
  else qtyInit();

  // Read by the card's inline onclick: until this is set, a tap on "+ ADD"
  // goes to the PDP rather than doing nothing.
  window.ppQty = { sync: qtySync };
})();
